'use client';
/* eslint-disable react-hooks/exhaustive-deps */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Script from 'next/script';
import { useNonce } from '@/providers/NonceProvider';

import { useFormContext } from 'react-hook-form';
import { type CheckoutPayload } from '@/schemas/checkout.schema';
import { apiUrl } from '@/lib/config';
import { useEvento } from '@/store/evento/evento';
import { useEventoContext } from '../../contexts/eventos';
import ENVS from '@/app/api/config/env';

const { MERCADO_PAGO } = ENVS;

type Props = {
  onSuccess?: (resp: any) => void;
  onError?: (err: any) => void;
  onLoading?: () => void;
  amount: number;
};

export type MetodoPagoTarjetaRef = {
  submitPayment: () => void;
  reset: () => void;
};

const PAYWAY_URL = apiUrl('/checkout');

// eslint-disable-next-line react/display-name
const MetodoPagoTarjeta = forwardRef<MetodoPagoTarjetaRef, Props>(
  ({ onSuccess, onError, onLoading, amount }, ref) => {
    const [sdkReady, setSdkReady] = useState(false);
    const [errMsg, setErrMsg] = useState<string | null>(null);
    const controllerRef = useRef<any>(null);

    const nonce = useNonce();

    // Verificar si el SDK ya está cargado al montar el componente
    useEffect(() => {
      const MP = (window as any).MercadoPago;
      if (MP && !sdkReady) {
        setSdkReady(true);
      }
    }, [sdkReady]);

    const { evento } = useEventoContext();

    const { state: funcion } = useEvento.use.funcion();
    const { state: entradas } = useEvento.use.entradas();
    const { state: comprador } = useEvento.use.comprador();

    const { state: reservaId } = useEvento.use.reserva();

    const description = `Entradas ${evento.nombre}`;

    const cleanup = () => {
      if (controllerRef.current) {
        try {
          controllerRef.current.unmount();
        } catch (error) {
          console.error('Error al destruir brick de MercadoPago:', error);
        }
        controllerRef.current = null;
      }
    };

    useImperativeHandle(ref, () => ({
      submitPayment: () => controllerRef.current?.submit?.(),
      reset: cleanup,
    }), []);

    useEffect(() => {
      let isMounted = true;
      (async () => {
        try {
          if (!sdkReady) {
            return;
          }

          // 1) Validaciones rápidas
          const pk = MERCADO_PAGO.PUBLIC_KEY;
          if (!pk) {
            const msg = 'Falta NEXT_PUBLIC_MP_PUBLIC_KEY (TEST-...)';
            console.error(msg);
            setErrMsg(msg);
            return;
          }

          if (!amount || amount <= 0) {
            const msg = 'El monto debe ser mayor a 0';
            console.warn(msg, { amount });
            setErrMsg(msg);
            return;
          }

          // 2) SDK
          const MP = (window as any).MercadoPago;
          if (!MP) {
            const msg = 'SDK de Mercado Pago no cargó';
            console.error(msg);
            setErrMsg(msg);
            return;
          }

          // 3) El contenedor debe existir
          const container = document.getElementById('card-payment-brick');
          if (!container) {
            const msg = 'No existe #card-payment-brick en el DOM';
            console.error(msg);
            setErrMsg(msg);
            return;
          }

          // Limpieza previa por si acaso
          cleanup();

          const mp = new MP(pk, { locale: 'es-AR' });
          const bricksBuilder = mp.bricks();

          const ctrl = await bricksBuilder.create('cardPayment', 'card-payment-brick', {
            initialization: { amount, payer: { email: comprador?.email } }, // number > 0
            customization: {
              visual: { style: { theme: 'dark' } },
              paymentMethods: { maxInstallments: 1 },
            },
            callbacks: {
              // 👇 Bricks entrega los campos en la raíz (no en formData)
              onSubmit: async (cardData: any) => {
                try {
                  if (!cardData.token) {
                    console.warn('[MP BRICK] submit sin token — ignorando');
                    return { error: true };
                  }

                  // Obtener datos del comprador del formulario
                  const payload = {
                    funcionId: funcion?.id,
                    entradas,
                    comprador: comprador,
                    gateway: 'mercadopago',
                    paymentData: {
                      ...cardData,
                      payer: { 
                        email: comprador?.email 
                      },
                    },
                    origin: 'online',
                    medioPago: 'credito',
                    reservaId: reservaId,
                  };

                  console.log('[MP] Enviando pago…');
                  onLoading?.();
                  const res = await fetch(PAYWAY_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    mode: 'cors',
                    body: JSON.stringify(payload),
                  });

                  const text = await res.text();
                  let json: any = {};
                  try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }

                  if (!res.ok) {
                    console.error('[MP] Pago rechazado:', res.status, json);
                    onError?.({ status: res.status, body: json });
                    return { error: true };
                  }

                  onSuccess?.(json);
                  return { success: true };
                } catch (err) {
                  console.error('[MP] Error inesperado al pagar:', err);
                  onError?.(err);
                  return { error: true };
                }
              },
              onError: (error: any) => {
                console.error('[MP BRICK] Error interno del Brick:', error);
                setErrMsg('Error del Brick: ' + (error?.message || 'desconocido'));
                onError?.(error);
              },
              onReady: () => {
                // Brick listo — sin log en producción
              },
            }
            ,
          });

          if (isMounted) {
            controllerRef.current = ctrl;
          } else {
            ctrl.unmount();
          }
        } catch (e: any) {
          console.error('[INIT ERROR]', e);
          setErrMsg(e?.message || 'Fallo creando el Brick');
          onError?.(e);
        }
      })();

      return () => {
        isMounted = false;
        cleanup();
      };
    }, [sdkReady, amount, comprador, description, onSuccess, onError, entradas]);

    return (
      <div className="space-y-3">
        <Script
          src="https://sdk.mercadopago.com/js/v2"
          strategy="afterInteractive"
          onLoad={() => {
            setSdkReady(true);
          }}
          nonce={nonce}
        />

        {/* Contenedor del Brick */}
        <div
          id="card-payment-brick"
          className="rounded-2xl bg-white/5 p-3 border border-white/10"
          style={{ minHeight: 120 }} // evita colapso si tarda
        />

        {/* Mensajes visibles para diagnóstico */}
        {errMsg && (
          <div className="text-xs text-red-300 bg-red-900/30 border border-red-700/40 rounded p-2">
            {errMsg}
          </div>
        )}
      </div>
    );
  }
);

export default MetodoPagoTarjeta;
